import React, { useState } from "react";
import { Image, ModalProps } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  StreakStatus,
  StreakConfigList,
} from "@nestcoinco/hyper-api-gateway-api-client";
import { useTranslation } from "react-i18next";

import "./StreakModal.scss";
import FlameIcon from "../../../icons/FlameIcon";
import ImageStreakGif from "../../../../images/streak-gif.gif";
import ImageHourglass from "../../../../images/hourglass.svg";
import StreakDays from "../../StreakDays/StreakDays";
import BottomDrawer from "../../../BottomDrawer/BottomDrawer";
import PrimaryActionButton from "../../../Buttons/PrimaryActionButton/PrimaryActionButton";
import StreakExpiringModal from "../StreakExpiringModal/StreakExpiringModal";
import { ROUTES } from "../../../../utils/constants";
import ArrowRightIcon from "../../../icons/ArrowRightIcon";

enum StreakState {
  NOT_STARTED = "not-started",
  JUST_STARTED = "just-started",
  IN_PROGRESS = "in-progress",
}

function getStreakState(days: number): StreakState {
  if (days === 0) return StreakState.NOT_STARTED;
  if (days === 1) return StreakState.JUST_STARTED;
  return StreakState.IN_PROGRESS;
}

function StreakNotStartedContent({ status }: { status: StreakStatus }) {
  const { t } = useTranslation(["common"]);

  return (
    <div className="streak-content-center">
      <div className="top-row position-relative d-flex justify-content-center">
        <span className="flame-container">
          <FlameIcon />
        </span>
      </div>

      <h2>{t("start_streak")}</h2>
      <p className="way-to-go-text">{t("play_a_game_to_start_a_streak")}</p>
      <div className="days-container w-100">
        <StreakDays status={status} />
      </div>
    </div>
  );
}

interface StreakActiveContentProps {
  status: StreakStatus;
  heading: string;
  subtext: string;
  daysBadge?: number;
}

function StreakActiveContent({
  status,
  heading,
  subtext,
  daysBadge,
}: StreakActiveContentProps) {
  return (
    <div className="streak-content-started">
      <div className="streak-gif-container">
        <Image src={ImageStreakGif} alt="streak" className="streak-gif" />
        {daysBadge != null && (
          <span className="streak-day-badge">{daysBadge}</span>
        )}
      </div>

      <h2>{heading}</h2>
      <p className="way-to-go-text">{subtext}</p>
      <div className="days-container w-100">
        <StreakDays status={status} />
      </div>
    </div>
  );
}

interface StreakModalProps extends ModalProps {
  status: StreakStatus;
  config: StreakConfigList;
  onHide: () => void;
  canShowTimeLeft?: boolean;
  timeLeftCopy?: string;
}

function StreakModal({ show, onHide, status, canShowTimeLeft, timeLeftCopy }: StreakModalProps) {
  const navigate = useNavigate();
  const { t } = useTranslation(["common", "games"]);
  const [isExpiringModalOpen, setIsExpiringModalOpen] = useState(false);

  const days = status.streakCount;
  const streakState = getStreakState(days);

  const buttonText =
    streakState === StreakState.NOT_STARTED
      ? t("games:game_results_page.go_to_games")
      : t("continue");

  const handlePrimaryAction = () => {
    if (streakState !== StreakState.NOT_STARTED) {
      onHide();
      return;
    }

    onHide();
    navigate(ROUTES.GAMES.ROOT);
  };

  const renderContent = () => {
    switch (streakState) {
      case StreakState.NOT_STARTED:
        return <StreakNotStartedContent status={status} />;
      case StreakState.JUST_STARTED:
        return (
          <StreakActiveContent
            status={status}
            heading={t("you_started_a_streak")}
            subtext={t(
              "play_games_everyday_to_extend_your_streak_skip_a_day_and_it_resets",
            )}
          />
        );
      case StreakState.IN_PROGRESS:
        return (
          <StreakActiveContent
            status={status}
            heading={t("day_streak", { count: days })}
            subtext={t("congrats_completed_days_in_a_row", { count: days })}
            daysBadge={days}
          />
        );
    }
  };

  return (
    <>
      <BottomDrawer
        onClose={onHide}
        isOpen={!!show}
        showCloseBtn={true}
        containerClass="streak-modal-drawer"
      >
        <div className="streak-info-modal">
          <div className="streak-main-content">
            {renderContent()}
            {canShowTimeLeft && timeLeftCopy && (
              <button
                className="streak-time-left-banner"
                onClick={() => setIsExpiringModalOpen(true)}
              >
                <span className="hourglass-wrapper">
                  <Image src={ImageHourglass} alt="hourglass" className="hourglass-img" />
                </span>
                <span className="banner-text">
                  {t("your_streak_resets_in")}{" "}
                  <strong>{timeLeftCopy}</strong>.
                </span>
                <ArrowRightIcon fill="white" />
              </button>
              )}
          </div>
          <PrimaryActionButton
            buttonClassName="bottom-cta"
            onClick={handlePrimaryAction}
            text={buttonText}
          />
        </div>
      </BottomDrawer>
      {canShowTimeLeft && timeLeftCopy && (
        <StreakExpiringModal
          show={isExpiringModalOpen}
          onHide={() => setIsExpiringModalOpen(false)}
          time={timeLeftCopy}
        />
      )}
    </>
  );
}

export default StreakModal;
